import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ClockIcon } from '@heroicons/react/24/outline'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'

interface NoApprovedTemplateNoticeProps {
  organization: string | null | undefined
  /** Fallback shown when the org has nothing in review — the caller's own
   * "create a template" guidance for a true dead end. */
  children: ReactNode
}

/**
 * Shown where a send is blocked because the org has no approved template. When a
 * template is already submitted and awaiting Meta's review, this explains the
 * wait in plain language and says the team will be told when it is approved,
 * rather than reading as a dead end. With nothing in review it defers to the
 * caller's guidance to create one.
 */
export const NoApprovedTemplateNotice = ({
  organization,
  children,
}: NoApprovedTemplateNoticeProps) => {
  const { t } = useTranslation()
  const { data: pendingPage, isLoading } = useQuery(whatsappTemplateQueries.pending(organization))

  // Hold the surface blank until we know which message is honest, so the
  // dead-end fallback never flashes before an "in review" state resolves.
  if (isLoading) return null

  const hasPending = (pendingPage?.results.length ?? 0) > 0
  if (!hasPending) return <>{children}</>

  return (
    <div className="flex flex-col items-start gap-1" data-testid="template-in-review">
      <p className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
        <ClockIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('chat.templateInReview')}
      </p>
      <p className="text-sm text-text-secondary">{t('chat.templateInReviewNotify')}</p>
    </div>
  )
}
